




















































































































































import { CreateElement, VNode } from 'vue';
import { Component, Prop, Model, Watch } from 'vue-property-decorator';
import { SwExtendedVue } from '../sw-extended-vue';
import _debounce from 'lodash/debounce';
import { dropdown, DropdownDirValues, EventCallbackParam } from '../dropdown-dir';
import { SwIcon } from '../sw-icon';
import { SwChip } from '../sw-chip';
import { domService } from '../dom-service';
import { SelectOption } from './types';
import SwSelectOption from './sw-select-option.vue';

const cssPrefix = 'sw-select';

const DATA_ATTR_RENDERED_INDEX = 'data-raw-rendered-row-index';
const VIRTUAL_SCROLL_PHASEOUT_MARGIN = 50; // rows
/** NOTE: have to match min height of SwSelctionOption */
const ROW_HEIGHT = 29;

/**
 * @event change `(optionId: string | string[])`
 */
@Component({
  directives: {
    dropdown,
  },
  components: {
    SwIcon,
    SwChip,
  },
})
export default class SwSelect extends SwExtendedVue {
  /** `string[]` means multiple selection mode */
  @Model('change', { type: [Array, String], required: true })
  value: string | string[];

  @Prop({ type: Boolean, default: false })
  disabled: boolean;

  @Prop({ type: Boolean, default: false })
  searchable: boolean;

  @Prop({ type: Array, required: true })
  options: SelectOption[];

  @Prop({ type: String, required: true })
  name: string;

  @Prop({ type: String, default: undefined })
  prefix: string | undefined;

  @Prop({ type: String, default: '' })
  placeholder: string;

  @Prop({ type: Number, default: Infinity })
  maxHeight: number;

  @Prop({ type: Number, default: Infinity })
  maxWidth: number;

  @Prop({ type: Boolean, default: false })
  full: boolean;

  @Prop({ type: String, default: 'light' })
  theme: 'dark' | 'light';

  cssPrefix = cssPrefix;
  scopeId = String(Math.floor(Math.random() * 100000000000));

  isDropdownVisible: boolean = false;
  width: number = 180; // in order to get the width changing reactive
  isFocus: boolean = false;
  searchTerm: string = '';
  kbHighlightIndex: number = -1;

  visibleRenderedIndexRange: { start: number; end: number; } = { start: 0, end: 0 };
  readyToHandleDropdownResizeOrScroll: boolean = false;
  searchInputFocussed: boolean = false;

  get physicalRenderIndexRange(): [number, number] {
    const startIndex = Math.max(0, this.visibleRenderedIndexRange.start - VIRTUAL_SCROLL_PHASEOUT_MARGIN);
    const endIndex = this.visibleRenderedIndexRange.end < Number.POSITIVE_INFINITY
    ? this.visibleRenderedIndexRange.end + VIRTUAL_SCROLL_PHASEOUT_MARGIN
    : Number.POSITIVE_INFINITY;

    return [startIndex, endIndex];
  }

  get isMultiple(): boolean {
    return Array.isArray(this.value);
  }

  get selectedOptions(): SelectOption[] {
    return this.options.filter((option) => {
      return Array.isArray(this.value) ? this.value.includes(option.id) : this.value === option.id;
    });
  }

  get filteredOptions(): SelectOption[] {
    let filteredOptions = this.options;

    if (!!this.searchTerm) {
      const lowerSearch = this.searchTerm.toLowerCase();
      filteredOptions = this.options.filter(option => (option.label.toString().toLowerCase().indexOf(lowerSearch) >= 0)); // tslint:disable-line
    }

    return filteredOptions;
  }

  get submitValue() {
    if (this.selectedOptions.length > 1) {
      return JSON.stringify(this.selectedOptions.filter(o => (o.value !== undefined))
        .map(o => (o.value)));
    }
    if (this.selectedOptions.length === 1 && this.selectedOptions[0].value) {
      return this.selectedOptions[0].value;
    }
    return '';
  }

  get optionsDropdownValue(): DropdownDirValues {
    return {
      withArrow: false,
      content: this.optionsDropdownContent,
      trigger: 'controlled',
      visible: !this.disabled && this.isDropdownVisible,
      placement: 'bottom-start',
      width: this.width,
      maxWidth: this.maxWidth,
      maxHeight: this.maxHeight,
      resizeCb: this.onDropdownResizeOrScroll,
      scrollCb: this.onDropdownResizeOrScroll,
      onClickOutsidePopper: () => { this.isDropdownVisible = false; },
    };
  }

  get throttleUpdateVisibleRenderedIndexRange() {
    return _debounce(
      this.updateVisibleRenderedIndexRange,
      350,
    );
  }

  get showSearchInput() {
    return this.searchable && (this.isFocus || this.isDropdownVisible);
  }

  get noTabIndex() {
    return this.disabled || (this.showSearchInput && this.searchInputFocussed);
  }

  @Watch('isFocus')
  private onIsFocusChange() {
    if (!this.searchable) { return; }
    if (this.isFocus) {
      if (this.isMultiple) {
        this.searchTerm = '';
      } else {
        this.searchTerm = '';
        /* Making the dropdown show all the options when search is enabled */
        // if (this.selectedOptions.length >= 1) {
        //   const option = this.selectedOptions[0];
        //   this.searchTerm = this.getOptionLabelStringRepresentation(option);
        // }
      }
    }
  }

  @Watch('showSearchInput')
  private onShowSearchInputChange() {
    this.searchInputFocussed = false;
  }

  @Watch('isDropdownVisible')
  private onDropdownVisibleChange() {
    if (!this.isDropdownVisible) {
      this.kbHighlightIndex = -1;
      if (this.searchable && this.isFocus) {
        /** we try to restore the searchTerm when it is still focused */
        if (this.selectedOptions.length >= 1) {
          const option = this.selectedOptions[0];
          this.searchTerm = this.getOptionLabelStringRepresentation(option);
        }
      }
      this.readyToHandleDropdownResizeOrScroll = false;
    } else {
      if (this.selectedOptions.length > 0) {
        const option = this.selectedOptions[0];
        const initialIndex = this.filteredOptions.findIndex(o => (o.id === option.id));

        this.visibleRenderedIndexRange.start = Math.max(initialIndex, 0);
        this.visibleRenderedIndexRange.end = Math.max(initialIndex, 0);

        this.$nextTick(() => {
          if (this.maxHeight === Infinity) {
            setTimeout(() => { this.scrollOptionIntoView(option); this.readyToHandleDropdownResizeOrScroll = true; }, 48);
          } else {
            this.scrollOptionIntoView(option);
            this.readyToHandleDropdownResizeOrScroll = true;
          }
        });
      } else {
        this.readyToHandleDropdownResizeOrScroll = true;
      }
    }
  }

  @Watch('kbHighlightIndex')
  private onKbHightlightIndexChange() {
    if (this.kbHighlightIndex < 0) { return; }
    const option = this.filteredOptions[this.kbHighlightIndex];
    if (!option) { return; }
    this.$nextTick(() => {
      this.scrollOptionIntoView(option);
    });
  }

  private renderDisplayLabels(h: CreateElement): VNode[] {
    const nodes: VNode[] = [];
    // const showSearchInput = this.searchable && (this.isFocus || this.isDropdownVisible);

    const searchInputNode = (
      <input
        class={[`${this.cssPrefix}__label-input`, 'sw-flexbox__flex', 'sw-typo-default']}
        placeholder={this.selectedOptions.length > 0 ? '' : this.placeholder}
        value={this.searchTerm}
        tabindex={-1}
        ref="searchInput"
        on-input={this.onSearchInput}
        on-focus={this.onSearchInputFocus}
        on-blur={this.onSearchInputBlur}
        on-click={this.onSearchInputClick}
      /> as VNode
    );

    if (!this.isMultiple) {

      if (this.showSearchInput) {
        nodes.push(searchInputNode);
      }
      if (this.selectedOptions.length >= 1) {
        const option = this.selectedOptions[0];

        nodes.push(
          <span class={[`${this.cssPrefix}__label-text`, 'sw-flexbox__flex']} style={{ display: this.showSearchInput ? 'none' : undefined }}>
          { typeof option.label === 'function' ? option.label(h) : option.label }
          </span> as VNode,
        );
      } else {
        nodes.push(
          <span
            class={[`${this.cssPrefix}__label-placeholder`, 'sw-flexbox__flex']}
            style={{ display: this.showSearchInput ? 'none' : undefined }}
          >
            { this.placeholder }
          </span> as VNode,
        );
      }
    } else {
      if (this.selectedOptions.length === 0) {
        nodes.push(
          <span
            class={[`${this.cssPrefix}__label-placeholder`, 'sw-flexbox__flex']}
            style={{ display: this.showSearchInput ? 'none' : undefined }}
          >
            { this.placeholder }
          </span> as VNode,
        );
      }
      this.selectedOptions.forEach((option) => {
        nodes.push(
          <SwChip
            class={[`${this.cssPrefix}__label-chip`, 'sw-flexbox__flex-none']}
            theme={this.theme}
            on-request-remove={(event: MouseEvent) => { event.stopPropagation(); this.onSelectOption(option); }}
          >
          { typeof option.label === 'function' ? option.label(h) : option.label }
          </SwChip> as VNode,
        );
      });

      if (this.showSearchInput) {
        nodes.push(searchInputNode);
      }
    }

    return nodes;
  }

  private optionsDropdownContent(h: CreateElement): VNode[] {// tslint:disable-line
    const nodes: VNode[] = [];
    const optionsCount = this.filteredOptions.length;

    for (
      let index = this.physicalRenderIndexRange[0];
      index < optionsCount && index <= this.physicalRenderIndexRange[1];
      index += 1
    ) {
      const option = this.filteredOptions[index];
      const isSelected = this.selectedOptions.findIndex(o => (o.id === option.id)) >= 0;

      const node = (
        <SwSelectOption
          key={option.id}
          id={`${this.cssPrefix}__option-${this.scopeId}-${option.id}`}
          {...{ attrs: { [DATA_ATTR_RENDERED_INDEX]: index } }}
          class={[
            `${this.cssPrefix}__option-${this.scopeId}`,
            {
              [`${cssPrefix}__row-rendered-first--${this.scopeId}`]: index === this.physicalRenderIndexRange[0],
              [`${cssPrefix}__row-rendered-last--${this.scopeId}`]: index === this.physicalRenderIndexRange[1],
            },
          ]}
          props-selected={isSelected}
          props-disabled={option.id === '' || !!option.disabled}
          props-highlight={this.kbHighlightIndex >= 0 && this.kbHighlightIndex === index}
          nativeOn-click={() => { this.onSelectOption(option); }}
          nativeOn-mouseenter={() => { this.kbHighlightIndex = -1; }}
          nativeOn-mouseleave={() => { this.kbHighlightIndex = -1; }}
        >
        {
          typeof option.label === 'function' ? option.label(h) : option.label
        }
        </SwSelectOption>
      ) as VNode;
      nodes.push(node);
    }

    if (this.physicalRenderIndexRange[0] > 0 && (optionsCount - 1) >= this.physicalRenderIndexRange[0]) {
      nodes.unshift(
        <div class={[`${cssPrefix}__leading-blank`]} style={{ height: `${this.physicalRenderIndexRange[0] * ROW_HEIGHT}px` }}></div> as VNode,
      );
    }
    if (this.physicalRenderIndexRange[1] < optionsCount - 1) {
      nodes.push(
        <div class={[`${cssPrefix}__trailing-blank`]}
          style={{ height: `${(optionsCount - 1 - this.physicalRenderIndexRange[1]) * ROW_HEIGHT}px` }}
        ></div> as VNode,
      );
    }

    return nodes;
  }

  private getOptionLabelStringRepresentation(option: SelectOption) {
    if (typeof option.label === 'string') { return option.label; }
    if (!option.labelStringRep) {
      throw new TypeError('option\'s label string representation is not defined');
    }
    return typeof option.labelStringRep === 'function' ? option.labelStringRep() : option.labelStringRep;
  }

  private scrollOptionIntoView(option: SelectOption) {
    const optionEl = document.getElementById(`${this.cssPrefix}__option-${this.scopeId}-${option.id}`);
    if (!optionEl) { return; }
    const scrollEl = optionEl.closest('.sw-scroll-view__view__cont'); // Note: very hacky way
    if (!scrollEl) { return; }

    const optionElRect = optionEl.getBoundingClientRect();
    const scrollElClientRect = scrollEl.getBoundingClientRect();
    const scrollElRect = {
      top: scrollElClientRect.top,
      left: scrollElClientRect.left,
      width: scrollEl.clientWidth,
      height: scrollEl.clientHeight,
    };

    if (optionElRect.top < scrollElRect.top) {
      scrollEl.scrollTop -= (scrollElRect.top - optionElRect.top);
    } else if ((optionElRect.top + optionElRect.height) > (scrollElRect.top + scrollElRect.height)) {
      scrollEl.scrollTop += (optionElRect.top + optionElRect.height) - (scrollElRect.top + scrollElRect.height);
    }
  }

  private getRowElementFromClientPoint(point: { x: number; y: number; }): HTMLElement | null {
    const el = document.elementFromPoint(point.x, point.y);
    if (!el) { return null; }
    const rowEl = el.closest(
      `${this.cssPrefix}__option-${this.scopeId}[${DATA_ATTR_RENDERED_INDEX}]`,
    );
    if (!rowEl) { return null; }
    return rowEl as HTMLElement;
  }

  private updateVisibleRenderedIndexRange(param: EventCallbackParam) {
    if (param.scrollHeight <= param.clientHeight) {
      this.visibleRenderedIndexRange.start = 0;
      this.visibleRenderedIndexRange.end = Math.round(param.clientHeight / ROW_HEIGHT);
      return;
    }

    const contRect = param.contEl.getBoundingClientRect();

    const firstEl = this.getRowElementFromClientPoint({ x: contRect.left + 2, y: contRect.top + 2 });
    const lastEl = this.getRowElementFromClientPoint({ x: contRect.left + 2, y: contRect.top + param.clientHeight - 2 });

    const firstIndex = (() => {
      if (firstEl) {
        return parseInt(firstEl.dataset['rawRenderedRowIndex'] || '', 10);
      }

      const firstRenderedEl = this.$el.querySelector(`${cssPrefix}__row-rendered-first--${this.scopeId}`) as HTMLElement;
      const lastRenderedEl = this.$el.querySelector(`${cssPrefix}__row-rendered-last--${this.scopeId}`) as HTMLElement;

      /** derive the firstIndex from the firstRenderedEl if possible */
      if (!firstRenderedEl) {
        return Math.floor(param.scrollTop / ROW_HEIGHT);
      }

      const contTop = param.scrollTop;
      const firstRenderedTop = firstRenderedEl.offsetTop;
      const firstRenderedIndex = parseInt(firstRenderedEl.dataset['rawRenderedRowIndex'] || '', 10);

      if (contTop < firstRenderedTop) {
        return Math.max(0, firstRenderedIndex - Math.ceil((firstRenderedTop - contTop) / ROW_HEIGHT));
      }

      /** derive the firstIndex from the lastRenderedEl if possible */
      if (!lastRenderedEl) {
        return Math.floor(param.scrollTop / ROW_HEIGHT);
      }

      const lastRenderedTop = lastRenderedEl.offsetTop + lastRenderedEl.offsetHeight;
      const lastRenderedIndex = parseInt(lastRenderedEl.dataset['rawRenderedRowIndex'] || '', 10);

      if (contTop > lastRenderedTop) {
        return lastRenderedIndex + Math.ceil((contTop - lastRenderedTop) / ROW_HEIGHT);
      }

      return Math.round((firstRenderedIndex + lastRenderedIndex) / 2);
    })();
    const lastIndex = lastEl
    ? parseInt(lastEl.dataset['rawRenderedRowIndex'] || '', 10)
    : Math.round(param.clientHeight / ROW_HEIGHT) + firstIndex;

    this.visibleRenderedIndexRange.start = firstIndex;
    this.visibleRenderedIndexRange.end = lastIndex;
  }

  private onClickBox() {
    if (this.disabled) { return; }
    this.isDropdownVisible = !this.isDropdownVisible;
  }

  private onSelectOption(option: SelectOption) {
    if (!this.isMultiple) {
      this.$emit('change', option.id);
      (this.$el as HTMLElement).focus();
      this.isDropdownVisible = false;
    } else {
      /** in the case of multiple selection mode, we already emit array of selected ids */
      const toIds = this.selectedOptions.map(o => (o.id));
      const index = toIds.indexOf(option.id);
      if (index >= 0) {
        toIds.splice(index, 1);
      } else {
        toIds.push(option.id);
      }
      this.$emit('change', toIds);
      (this.$el as HTMLElement).focus();
    }
  }

  private onFocus() {
    this.isFocus = true;
    if (this.searchable) {
      this.$nextTick(() => {
        if (!!this.$refs.searchInput) {
          (this.$refs.searchInput as HTMLElement).focus();
        }
      });
    }
  }

  private onBlur() {
    if (!this.searchable) {
      this.isFocus = false;
    }
  }

  private onKeydown(event: KeyboardEvent) {
    let move = 0;
    switch (event.key) {
      case 'Up':
      case 'ArrowUp': {
        move = -1;
        event.preventDefault();
        break;
      }
      case 'Down':
      case 'ArrowDown': {
        move = 1;
        event.preventDefault();
        break;
      }
      case 'Tab': {
        this.isDropdownVisible = false;
        break;
      }
    }

    if (move !== 0) {
      this.kbHighlightIndex = Math.min(
        this.filteredOptions.length - 1,
        Math.max(
          0,
          this.kbHighlightIndex + move,
        ),
      );
      this.isDropdownVisible = true;
    }
  }

  private onKeyup(event: KeyboardEvent) {
    switch (event.key) {
      case 'Esc':
      case 'Escape': {
        if (this.$refs.searchInput) {
          (this.$refs.searchInput as HTMLElement).blur();
        } else {
          (this.$el as HTMLElement).blur();
        }
        this.isDropdownVisible = false;
        break;
      }
      case 'Enter': {
        if (this.kbHighlightIndex >= 0) {
          const option = this.filteredOptions[this.kbHighlightIndex];
          if (!option) { break; }
          this.isFocus = false;
          this.$nextTick(() => {
            this.onSelectOption(option);
          });
        }
        break;
      }
    }
  }

  private onSearchInputFocus() {
    this.isFocus = true;
    this.searchInputFocussed = true;
  }

  private onSearchInputBlur() {
    this.isFocus = false;
    this.searchInputFocussed = false;
  }

  private onSearchInput(event: MouseEvent) {
    this.searchTerm = (event.target as HTMLInputElement).value;
    this.isDropdownVisible = true;
  }

  private onSearchInputClick(event: MouseEvent) {
    if (this.isDropdownVisible) {
      event.stopPropagation();
    }
  }

  private onDropdownResizeOrScroll(param: EventCallbackParam) {
    if (!this.readyToHandleDropdownResizeOrScroll) { return; }
    this.throttleUpdateVisibleRenderedIndexRange(param);
  }

  private updateSizes() {
    this.width = (this.$el as HTMLElement).offsetWidth;
  }

  private mounted() {
    this.$nextTick(() => {
      this.updateSizes();
    });
    domService.resizeObserver.on(this.$el as HTMLElement, this.updateSizes);
  }

  private updated() {
    this.$nextTick(() => {
      this.updateSizes();
    });
  }

  private beforeDestroy() {
    this.throttleUpdateVisibleRenderedIndexRange.cancel();
    domService.resizeObserver.off(this.$el as HTMLElement, this.updateSizes);
  }

  private render(h: CreateElement): VNode {// tslint:disable-line
    const rootNode = (
      <div
        class={[`${this.cssPrefix}`, `${this.cssPrefix}--${this.theme}`, 'sw-typo-default',
          'sw-flexbox', 'sw-flexbox--inline', 'sw-flexbox--center-items',
          {
            [`${this.cssPrefix}--focus`]: this.isFocus,
            [`${this.cssPrefix}--disabled`]: this.disabled,
            [`${this.cssPrefix}--full`]: this.full,
            [`${this.cssPrefix}--auto-height`]: this.isMultiple,
            [`${this.cssPrefix}--top-padding`]: !this.isMultiple,
          },
        ]}
        v-dropdown={this.optionsDropdownValue}
        tabindex={this.noTabIndex ? undefined : 0}
        on-click={this.onClickBox}
        on-focus={this.onFocus}
        on-blur={this.onBlur}
        on-keydown={this.onKeydown}
        on-keyup={this.onKeyup}
      >
        <input type="hidden" name={name} value={this.submitValue} disabled={this.disabled} />
        { !!this.prefix ? <div class={[`${cssPrefix}__label-prefix`, 'sw-flexbox__flex-none']}>{ this.prefix }:</div> : null }
        <div
          class={[`${this.cssPrefix}__label-cont`, 'sw-flexbox__flex',
            'sw-flexbox', 'sw-flexbox--center-items', 'sw-flexbox--wrap',
          ]}
        >{this.renderDisplayLabels(h)}</div>
        <div
          class={[`${this.cssPrefix}__icon`, { [`${this.cssPrefix}__icon--active`]: this.isDropdownVisible },
            'sw-flexbox', 'sw-flexbox--center-items', 'sw-flexbox--center-justify',
          ]}
        >
          <sw-icon name="arrow-up" size={14} rotate={180} ></sw-icon>
        </div>
      </div>
    );

    return rootNode as VNode;
  }
}
